/**
 * @author piotrzurek
 * @since 2022-7-20
 */

import * as React from "react";
import styled from "styled-components";

import { FlowPanel } from "@ui/Molecules/FlowPanel";
import { Tag } from "@ui/Atoms/Tag";
import { rem } from "@ui/helpers";
import { TestWrapper } from "@ui/Atoms/TestWrapper";
import { SectionMenu } from "@ui/Atoms/SectionMenu";

import { textToSnake } from "@services/TextToSnake";

type ReasonItem = {
  testId?: string;
  name: string;
  [key: string]: any;
};

interface Props<T extends ReasonItem = ReasonItem> {
  heading: string;
  description?: string;
  reasons: T[];
  isRootReason: boolean;
  onReasonSelection: (reason: T) => void;
  testId?: string;
  className?: string;
  withPreOptions?: boolean;
  preHeading?: string;
  preList?: T[];
  withMultipleOptions?: boolean;
  selectedOptions?: string[];
  onPreOptionClick?: (name: string) => void;
  disabledReasons?: boolean;
}

const ReasonSelector = (props: Props): React.ReactElement => (
  <TestWrapper testId={props.testId}>
    <div className={props.className}>
      {props.withPreOptions && (
        <OptionsWrapper>
          <OptionsTitle type="h2" size={"large"}>
            {props.preHeading}
          </OptionsTitle>

          <EditList>
            {props.preList?.map(current => (
              <SectionMenu.Item
                key={current.name}
                testId={current.testId}
                icon={<current.icon width={28} variant="line" />}
                onClick={() => props.onPreOptionClick?.(current.name)}
                hasLink
              >
                {current.name}
              </SectionMenu.Item>
            ))}
          </EditList>
        </OptionsWrapper>
      )}

      <HeadingContainer>
        <FlowPanel.Title
          type="h2"
          size={props.isRootReason && props.withPreOptions ? "large" : "small"}
        >
          {props.heading}
        </FlowPanel.Title>
        {!!props.description && <Description>{props.description}</Description>}
      </HeadingContainer>

      <Reasons>
        {props.reasons.map((current, index) => (
          <Reason
            testId={
              current.key
                ? `${props.testId}_${current.key}`
                : textToSnake(current.name)
            }
            key={index}
            onClick={() => props.onReasonSelection(current)}
            size="l"
            isActive={
              props.withMultipleOptions &&
              props.selectedOptions?.includes(current.key)
            }
            disabled={props.disabledReasons}
          >
            {current.name}
          </Reason>
        ))}
      </Reasons>
    </div>
  </TestWrapper>
);

export { ReasonSelector };

const OptionsWrapper = styled.div`
  margin-bottom: ${props => props.theme.margins.base_x5};
`;

const OptionsTitle = styled(FlowPanel.Title)`
  margin-bottom: ${props => props.theme.margins.base_x4};
`;

const Reasons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${props => props.theme.margins.base_x2};
  width: fit-content;
  margin: 0 auto;
`;

const Reason = styled(Tag)`
  width: 100%;
  min-width: ${rem(250)};
`;

const EditList = styled(SectionMenu)`
  width: 100%;
  padding: 0;
  margin: 0 auto;

  ${props => props.theme.breakpoints.tablet} {
    min-width: ${rem(327)};
    width: fit-content;
  }
`;

const HeadingContainer = styled.div`
  margin-bottom: ${props => props.theme.margins.base_x3};

  ${props => props.theme.breakpoints.tablet} {
    margin-bottom: ${props => props.theme.margins.base_x4};
  }
`;

const Description = styled(FlowPanel.Description)`
  margin-bottom: 0;
`;
